/**
 * Vista de videollamada (cámara del dispositivo / Grandstream).
 * No es la isla del timbre. Se abre solo cuando el usuario toca el botón de cámara.
 */
import React, { useEffect, useRef } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import {
  CallEnd,
  Cameraswitch,
  Hearing,
  Mic,
  MicOff,
  Videocam,
  VideocamOff,
  VolumeUp,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { sipSoftphone } from '../../services/softphone/SipSoftphone';

const ACCENT = '#2391FF';

function isMobileUa(): boolean {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
}

function peerInitials(label: string): string {
  const parts = label.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }
  const one = parts[0] || '?';
  return one.slice(0, 2).toUpperCase();
}

export interface PhoneVideoStageProps {
  peerLabel: string;
  statusLabel: string;
  cameraOn: boolean;
  facingUser: boolean;
  hasRemoteVideo: boolean;
  muted: boolean;
  speaker: boolean;
  onToggleMute: () => void;
  onToggleSpeaker: () => void;
  onToggleCamera: () => void;
  onFlipCamera: () => void;
  onHangup: () => void;
}

const PhoneVideoStage: React.FC<PhoneVideoStageProps> = ({
  peerLabel,
  statusLabel,
  cameraOn,
  facingUser,
  hasRemoteVideo,
  muted,
  speaker,
  onToggleMute,
  onToggleSpeaker,
  onToggleCamera,
  onFlipCamera,
  onHangup,
}) => {
  const { t } = useTranslation();
  const localRef = useRef<HTMLVideoElement | null>(null);
  const remoteRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    sipSoftphone.bindVideoElements(localRef.current, remoteRef.current);
    return () => sipSoftphone.bindVideoElements(null, null);
  }, []);

  const ctrl = (active: boolean, danger = false) => ({
    width: 54,
    height: 54,
    color: '#fff',
    background: danger
      ? 'linear-gradient(180deg, #ff5252 0%, #c62828 100%)'
      : active
        ? `linear-gradient(180deg, ${ACCENT} 0%, #1565c0 100%)`
        : 'rgba(255,255,255,0.12)',
    border: danger || active ? 'none' : '1px solid rgba(255,255,255,0.18)',
    boxShadow: danger
      ? '0 8px 22px rgba(198,40,40,0.45)'
      : active
        ? `0 8px 22px ${ACCENT}55`
        : 'none',
    backdropFilter: 'blur(12px)',
    '&:hover': {
      background: danger
        ? 'linear-gradient(180deg, #ff6e6e 0%, #d32f2f 100%)'
        : active
          ? `linear-gradient(180deg, #42a5ff 0%, ${ACCENT} 100%)`
          : 'rgba(255,255,255,0.2)',
    },
  });

  return (
    <Box
      data-phone-no-swipe=""
      sx={{
        position: 'relative',
        flex: 1,
        minHeight: 0,
        width: '100%',
        overflow: 'hidden',
        background: '#05070b',
      }}
    >
      <video
        ref={remoteRef}
        autoPlay
        playsInline
        muted
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          background: '#000',
          display: hasRemoteVideo ? 'block' : 'none',
        }}
      />

      {!hasRemoteVideo && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            background: 'radial-gradient(ellipse at 50% 30%, #1a3350 0%, #05070b 70%)',
          }}
        >
          <Box
            sx={{
              width: 92,
              height: 92,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `linear-gradient(160deg, ${ACCENT} 0%, #0d47a1 100%)`,
              boxShadow: `0 12px 40px ${ACCENT}44`,
              color: '#fff',
              fontWeight: 700,
              fontSize: '1.7rem',
              letterSpacing: 1,
            }}
          >
            {peerInitials(peerLabel)}
          </Box>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.25rem' }}>
            {peerLabel}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.85rem' }}>
            {t('softphone.video.noCamera')}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          pt: 2,
          pb: 5,
          px: 2.25,
          background: 'linear-gradient(180deg, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0) 100%)',
          pointerEvents: 'none',
        }}
      >
        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.2rem', letterSpacing: 0.3 }}>
          {peerLabel}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.78rem', mt: 0.25 }}>
          {statusLabel}
        </Typography>
      </Box>

      <Box
        sx={{
          position: 'absolute',
          right: 14,
          bottom: 118,
          width: 'min(32vw, 128px)',
          aspectRatio: '3 / 4',
          borderRadius: '16px',
          overflow: 'hidden',
          background: '#111',
          border: '1.5px solid rgba(255,255,255,0.55)',
          boxShadow: '0 10px 28px rgba(0,0,0,0.55)',
          display: cameraOn ? 'block' : 'none',
        }}
      >
        <video
          ref={localRef}
          autoPlay
          playsInline
          muted
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transform: facingUser ? 'scaleX(-1)' : 'none',
            background: '#111',
          }}
        />
        <Typography
          sx={{
            position: 'absolute',
            left: 6,
            bottom: 5,
            color: '#fff',
            fontSize: '0.62rem',
            fontWeight: 600,
            textShadow: '0 1px 4px rgba(0,0,0,0.8)',
          }}
        >
          {t('softphone.video.you')}
        </Typography>
      </Box>

      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          pt: 6,
          pb: 2.25,
          px: 1.5,
          display: 'flex',
          justifyContent: 'center',
          background: 'linear-gradient(0deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0) 100%)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.1,
            px: 1.25,
            py: 1,
            borderRadius: 8,
            background: 'rgba(12,16,22,0.55)',
            border: '1px solid rgba(255,255,255,0.1)',
            backdropFilter: 'blur(18px)',
          }}
        >
          <IconButton
            onClick={onToggleMute}
            aria-label={t('softphone.controls.mute') as string}
            sx={ctrl(muted, muted)}
          >
            {muted ? <MicOff /> : <Mic />}
          </IconButton>
          <IconButton
            onClick={onToggleSpeaker}
            aria-label={t('softphone.controls.speaker') as string}
            sx={ctrl(speaker)}
          >
            {speaker ? <VolumeUp /> : <Hearing />}
          </IconButton>
          <IconButton
            onClick={onToggleCamera}
            aria-label={t(cameraOn ? 'softphone.video.cameraOff' : 'softphone.video.cameraOn') as string}
            sx={ctrl(true)}
          >
            {cameraOn ? <Videocam /> : <VideocamOff />}
          </IconButton>
          {isMobileUa() ? (
            <IconButton
              onClick={onFlipCamera}
              aria-label={t('softphone.video.flip') as string}
              sx={ctrl(false)}
            >
              <Cameraswitch />
            </IconButton>
          ) : null}
          <IconButton
            onClick={onHangup}
            aria-label={t('softphone.controls.hangup') as string}
            sx={{ ...ctrl(false, true), width: 60, height: 60 }}
          >
            <CallEnd sx={{ fontSize: 28 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default PhoneVideoStage;
